/**
 * Plain-language verdict for the NEXT UI. Owns its own copy + sentence assembly
 * in every locale. The ONLY thing it borrows is the engine's deterministic
 * ranking (`computeVerdictRanking`) so the headline can never contradict the
 * analysis.
 *
 * The wording is bound to what the tool can actually do OFFLINE, on this device:
 *  - `legitimate` is only ever awarded when a signature the message carries is
 *    verified locally with math (DKIM body-hash recompute / S-MIME / PGP) AND
 *    the addresses align. So the copy may honestly say "checks out on this
 *    device".
 *  - `mixed` is the #24 case: the addresses look fine, but the only "passes"
 *    are relay-reported (SPF / DKIM key / DMARC need DNS this tool never
 *    reaches). So the copy says, plainly, that nothing HERE can prove the
 *    sender: and the advice explains why.
 *  - `suspicious` / `malicious` follow the local spoof signals.
 */
import { computeVerdictRanking } from "../derivations/verdict-ranking";
import type { EmailAnalysis } from "../email-analysis";
import { modeAllowsNetwork } from "../verification";
import type { Locale } from "./i18n";
import { isHardFail, senderLabel } from "./selectors";

export type Tier = "legitimate" | "mixed" | "suspicious" | "malicious";

export interface Verdict {
  tier: Tier;
  /** Headline sentence: what the message claims and whether it holds up here. */
  sentence: string;
  /** One short paragraph of guidance. */
  advice: string;
  /** Resolved claimed-sender label. */
  sender: string;
}

interface VerdictCopy {
  sentence: (tier: Tier, sender: string, failed: string[]) => string;
  advice: Record<Tier, string>;
  /**
   * Mixed-tier advice when the analysis ran in a mode that IS allowed to
   * reach DNS. The default mixed advice says the tool "never touches" DNS,
   * which is only honest for the offline modes; this variant keeps the
   * relay-reported caveat but tells the truth about live lookups.
   */
  adviceMixedNetwork: string;
  /**
   * Neutral, name-free sender label for redacted surfaces (the embed). It must
   * read naturally in every tier sentence AND carry no display name, domain, or
   * address: so a shareable panel never narrates who the message claims to be.
   */
  redactedSender: string;
  /**
   * Last-resort sender label when the message carries no display name, domain,
   * or address at all. Must read naturally after the sentence's
   * `from`/`von`/`de`/`da` preposition.
   */
  unknownSender: string;
}

/** `(X, Y failed)` suffix, localised, or "" when no methods hard-failed. */
function failSuffix(failed: string[], word: string): string {
  return failed.length ? ` (${failed.join(", ")} ${word})` : "";
}

const COPY: Record<Locale, VerdictCopy> = {
  en: {
    sentence: (tier, sender, failed) => {
      switch (tier) {
        case "legitimate":
          return `This message really is from ${sender}: a signature it carries checks out on this device, and the addresses line up.`;
        case "mixed":
          return `This message says it is from ${sender}, but nothing on this device can prove that.`;
        case "suspicious":
          return `This message claims to be from ${sender}, and several things about it do not line up${failSuffix(failed, "failed")}.`;
        case "malicious":
          return `This message claims to be from ${sender}, but the sender is forged${failSuffix(failed, "failed")}.`;
      }
    },
    advice: {
      legitimate:
        "Authentication holds and the addresses align. Verification is not endorsement; the usual caution about urgent or unusual requests still applies.",
      mixed:
        "Any SPF or DKIM pass here is the receiving server's word, copied from the headers, not a check this tool re-runs. Confirming the sender would need DNS it never touches, so verify through a channel you already trust before you act.",
      suspicious:
        "The signals conflict. Do not follow links or open attachments until you have confirmed the sender another way.",
      malicious:
        "Authentication fails and the address is impersonated. Do not reply, click, or open anything. Report it and move on.",
    },
    adviceMixedNetwork:
      "Any SPF or DKIM pass here is the receiving server's word, copied from the headers, not a check this tool re-runs. Live DNS lookups can fetch the published records, but records alone cannot prove who sent this message; verify through a channel you already trust before you act.",
    redactedSender: "the sender it names",
    unknownSender: "an unknown sender",
  },
  de: {
    sentence: (tier, sender, failed) => {
      switch (tier) {
        case "legitimate":
          return `Diese Nachricht stammt wirklich von ${sender}: Eine enthaltene Signatur wird auf diesem Gerät bestätigt, und die Adressen passen zusammen.`;
        case "mixed":
          return `Diese Nachricht gibt vor, von ${sender} zu stammen, doch auf diesem Gerät lässt sich das nicht beweisen.`;
        case "suspicious":
          return `Diese Nachricht gibt vor, von ${sender} zu stammen, und mehreres passt nicht zusammen${failSuffix(failed, "fehlgeschlagen")}.`;
        case "malicious":
          return `Diese Nachricht gibt vor, von ${sender} zu stammen, doch der Absender ist gefälscht${failSuffix(failed, "fehlgeschlagen")}.`;
      }
    },
    advice: {
      legitimate:
        "Die Authentifizierung hält und die Adressen stimmen überein. Bestätigung ist keine Empfehlung; bei dringenden oder ungewöhnlichen Bitten bleibt Vorsicht geboten.",
      mixed:
        "Jeder SPF- oder DKIM-Pass hier ist die Aussage des empfangenden Servers, aus den Kopfzeilen kopiert, keine Prüfung, die MIME Smith wiederholt. Den Absender zu bestätigen bräuchte DNS, das es nie berührt; prüfe daher über einen Kanal, dem du bereits vertraust, bevor du handelst.",
      suspicious:
        "Die Signale widersprechen sich. Folge keinen Links und öffne keine Anhänge, bis du den Absender auf anderem Weg bestätigt hast.",
      malicious:
        "Die Authentifizierung schlägt fehl und die Adresse ist gefälscht. Nicht antworten, nicht klicken, nichts öffnen. Melde die Nachricht und lass sie liegen.",
    },
    adviceMixedNetwork:
      "Jeder SPF- oder DKIM-Pass hier ist die Aussage des empfangenden Servers, aus den Kopfzeilen kopiert, keine Prüfung, die MIME Smith wiederholt. Live-DNS-Abfragen können die veröffentlichten Einträge holen, doch Einträge allein beweisen nicht, wer diese Nachricht gesendet hat; prüfe daher über einen Kanal, dem du bereits vertraust, bevor du handelst.",
    redactedSender: "dem angezeigten Absender",
    unknownSender: "einem unbekannten Absender",
  },
};

/**
 * The claimed sender for the headline. `senderLabel` resolves the From header
 * (name, else domain, else address); the spoof layer's own summary backfills
 * each rung when From carried nothing there, and the caller's localized
 * placeholder is the last resort.
 */
function resolveSender(analysis: EmailAnalysis, unknown: string): string {
  const fromLabel = senderLabel(analysis);
  if (fromLabel) return fromLabel;
  const s = analysis.spoof.senderSummary;
  return s.displayName?.trim() || s.domain?.trim() || s.address?.trim() || unknown;
}

function methods(analysis: EmailAnalysis, want: (r: string) => boolean): string[] {
  const seen = new Set<string>();
  for (const block of analysis.auth)
    for (const e of block.entries) if (want(e.result)) seen.add(e.method.toLowerCase());
  // ARC-SPF / ARC-DKIM / ARC-DMARC are the ARC chain re-reporting the same
  // three methods. Listing all five turns the headline into a wall
  // ("DKIM, DMARC, ARC, ARC-DMARC, ARC-DKIM failed") that says one thing five
  // ways, so they collapse into ARC. Nothing is lost: the Authentication
  // section still shows every chip, per method and per signing domain.
  const collapsed = new Set<string>();
  for (const m of seen) collapsed.add(m.startsWith("arc-") ? "arc" : m);
  const order = ["spf", "dkim", "dmarc", "arc", "bimi"];
  const out: string[] = [];
  for (const m of order) if (collapsed.has(m)) out.push(m.toUpperCase());
  for (const m of collapsed) if (!order.includes(m)) out.push(m.toUpperCase());
  return out;
}

export interface VerdictOptions {
  /**
   * Redact the claimed sender: substitute a neutral, name-free label for the
   * real display name / domain / address. Set on shareable, default-redacted
   * surfaces (the `#embed` panel) so the verdict sentence never narrates who
   * the message claims to be. Off everywhere the full report shows real PII.
   */
  redactSender?: boolean;
  /**
   * Drop the failed-method suffix from the sentence.
   *
   * The report earns "(DKIM, DMARC, ARC failed)": its reader is looking at the
   * Authentication section two screens down. Triage does not. That view exists
   * to be handed to someone who is not an analyst, and three protocol acronyms
   * in the headline are exactly the jargon it is there to remove. The finding
   * is unchanged either way; only the naming of the machinery goes.
   */
  plain?: boolean;
}

export function buildVerdict(
  analysis: EmailAnalysis,
  locale: Locale = "en",
  opts: VerdictOptions = {},
): Verdict {
  const tier = computeVerdictRanking(analysis).verdict;
  const copy = COPY[locale] ?? COPY.en;
  const sender = opts.redactSender
    ? copy.redactedSender
    : resolveSender(analysis, copy.unknownSender);
  const failed = opts.plain ? [] : methods(analysis, isHardFail);

  // The mixed advice claims the tool "never touches" DNS; when the analysis
  // provably ran in a network-allowed mode that sentence would be a lie, so
  // the honest live-lookup variant takes over.
  const mode = analysis.provenance?.verificationMode;
  const advice =
    tier === "mixed" && mode && modeAllowsNetwork(mode)
      ? copy.adviceMixedNetwork
      : copy.advice[tier];

  return {
    tier,
    sentence: copy.sentence(tier, sender, failed),
    advice,
    sender,
  };
}
